import config from '../../app/config';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { docTypes } from '../../store/reducers/dicts';
import { useTranslation } from 'react-i18next';
import AgyleButton from '../ui/buttons/agyleButton';
import React, { useEffect, useState } from 'react';
import { patient as patientPreferences, savePref } from '../../store/reducers/preferences';
import _ from "lodash";
import API from '../../app/api';
import { user } from '../../store/reducers/user';
import { setSnackbar } from '../../store/reducers/globals';
import { v4 as uuidv4 } from 'uuid';
import useOpenState from '../../hooks/useOpenState';

import SmartViewItem from './smartViewComponents/SmartViewItem';
import CreateSmartViewDialog from './smartViewComponents/CreateSmartViewDialog';
import ShareSmartViewDialog from './smartViewComponents/ShareSmartViewDialog';
import ImageDisplayDialog from './smartViewComponents/ImageDisplayDialog';
import MessageBox from '../../util/messageBox';

const useStyles = makeStyles((theme) => ({
    modalBody: {
        width: "calc(100vw - 180px)",
        background: "#3D3E40",
        height: "98vh",
        borderLeft: "1px solid black",
        paddingLeft: 40,
    },
    title: {
        color: "#FFF",
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: 500,
        marginBottom: 15,
        marginTop: 20,
    },
    footer: {
        display: "inline-flex",
        width: "95%",
        justifyContent: "space-between",
        marginBottom: 10
    },
    bodyStyle: {
        backgroundColor: theme.palette.mode === "dark" ? "#3D3E40" : "#f5f5f5",
    },
    displayImageContainer: {
        marginTop: 30,
        height: "50%",
        overflow: "auto"
    },
    dialogContentStyle: {
        backgroundColor: theme.palette.mode === "dark" ? "#252525" : "#ebebeb"
    },
    secondLine: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 10
    }
}));

function SmartView(props) {
    const classes = useStyles();
    const docTypesDict = useSelector(docTypes);
    const { t, i18n } = useTranslation();

    const viewDisplay = [
        { name: "OS->OD", value: 1 },
        { name: "OS->OD->OU", value: 2 },
        { name: "OD->OS", value: 3 },
        { name: "OD->OS->OU", value: 4 },
        { name: "OU", value: 5 },
        { name: "Open related", value: 6 },
    ];

    const smartViewItemInit = {
        id: uuidv4() + '',
        version: 1,
        name: "",
        mainTest: "",
        steps: []
    };

    const patientPreferencesObject = useSelector(patientPreferences);
    const [smartViewSet, setSmartViewSet] = React.useState([]);
    const [originalSmartViewSet, setOriginalSmartViewSet] = React.useState([]);
    const [smartViewDefault, setSmartViewDefault] = React.useState({});
    const dispatch = useDispatch();

    const [displayImagePref, setDisplayImagePreferences] = React.useState({});
    const [selectedStepToManage, setSelectedStepToManage] = useState(null);
    const [isDisplayDialogOpen, openDisplayDialog, closeDisplayDialog] = useOpenState(false, true);

    const [isModalOpen, openModal, closeModal] = useOpenState(false, true);
    const [newSmartView, setNewSmartView] = useState(smartViewItemInit);
    const [expandedSet, setExpandedSet] = React.useState({});

    const [isModalShareOpen, openShareModal, closeShareModal] = useOpenState(false, true);
    const [selectedSmartViewToShare, setSelectedSmartViewToShare] = useState(null);
    const [viewFilter, setViewFilter] = useState('all');

    const [isDeleteSmartViewModalOpen, openDeleteSmartViewModal, closeDeleteSmartViewModal] = useOpenState(false);
    const [doDeleteSmartViewCallback, setDoDeleteSmartViewCallback] = React.useState(() => {});
    const [deleteSmartViewMessage, setDeleteSmartViewMessage] = React.useState("");

    const userObject = useSelector(user);

    useEffect(() => {
        let smartViewTmp = _.cloneDeep(patientPreferencesObject.smartView);
        smartViewTmp.forEach((sm) => {
            if (!sm.hasOwnProperty("id")) {
                sm.id = uuidv4() + "";
            }
        })
        setSmartViewSet(smartViewTmp);
        setOriginalSmartViewSet(_.cloneDeep(smartViewTmp));
        setSmartViewDefault(_.cloneDeep(patientPreferencesObject.smartViewDefault));

        setDisplayImagePreferences((prefOld) => {
            let pref = _.cloneDeep(patientPreferencesObject.studiesTypeViews);
            let newPref = {};

            docTypesDict.forEach((item) => {
                if (config.studiesType)
                    newPref[item.name] = config.studiesType[item.name]
                if (pref.hasOwnProperty(item.name))
                    newPref[item.name] = pref[item.name]
            });
            return newPref;
        });
    }, [patientPreferencesObject]);

    const handleDeleteStep = (index, viewId) => {
        let data = _.cloneDeep(smartViewSet);
        let viewIndex = data.findIndex((v) => v.id === viewId);
        if (viewIndex === -1) return;
        data[viewIndex].steps.splice(index, 1);
        setSmartViewSet(data);
    };

    const handleDeleteNewStep = (index) => {
        if(index === -1) return;
        let data = _.cloneDeep(newSmartView);
        data.steps.splice(index, 1);
        setNewSmartView(data);
    };

    const handleDeleteSmartView = (viewId) => {
        let viewItem = smartViewSet.find((v) => v.id === viewId);
        if (!viewItem) return;

        setDeleteSmartViewMessage(t(`Are you sure you want to delete '${viewItem.name}' Smart View?`));
        setDoDeleteSmartViewCallback(() => () => {
            doHandleDeleteSmartView(viewId);
            closeDeleteSmartViewModal();
        });
        openDeleteSmartViewModal();
    }

    const doHandleDeleteSmartView = (viewId) => {

        let dataSV = _.cloneDeep(smartViewSet);
        let viewIndex = dataSV.findIndex((v) => v.id === viewId);
        if (viewIndex === -1) return;

        dataSV.splice(viewIndex, 1);
        setSmartViewSet(dataSV);

        let data = _.cloneDeep(patientPreferencesObject);

        data.smartView = dataSV;
        data.studiesTypeViews = displayImagePref;
        data.smartViewDefault = smartViewDefault;

        dispatch(savePref(data));

    };

    const handleCopySmartView = (view) => {
        let data = _.cloneDeep(smartViewSet);
        let copedSmartView = {
            name: view.name + " (copy)",
            id: uuidv4() + '',
            version: 1,
            mainTest: view.mainTest,
            steps: _.cloneDeep(view.steps)
        };


        data.push(_.cloneDeep(copedSmartView));
        setSmartViewSet(data);


        let pref = _.cloneDeep(patientPreferencesObject);
        pref.smartView = data;
        pref.studiesTypeViews = displayImagePref;
        pref.smartViewDefault = smartViewDefault;
        dispatch(savePref(pref));
    }

    const handleSetHistory = (index, viewId, value) => {
        let data = _.cloneDeep(smartViewSet);
        let viewIndex = data.findIndex((v) => v.id === viewId);
        if (viewIndex === -1) return;
        data[viewIndex].steps[index].type = (value ? "history" : "current");
        setSmartViewSet(data);
    };

    const handleSetHistoryNew = (index, value) => {
        let data = _.cloneDeep(newSmartView);
        data.steps[index].type = (value ? "history" : "current");
        setNewSmartView(data);
    };

    const handleSetTest = (index, viewIndex, value) => {
        let data = _.cloneDeep(smartViewSet);
        data[viewIndex].steps[index].test = value;

        if (index !== 0) {
            data[viewIndex].steps[index].view = null;

            if (config.studiesType && config.studiesType[value])
                data[viewIndex].steps[index].view = config.studiesType[value]

            if (patientPreferencesObject.studiesTypeViews[value])
                data[viewIndex].steps[index].view = patientPreferencesObject.studiesTypeViews[value]
        }

        if (index === 0) data[viewIndex].mainTest = value;

        setSmartViewSet(data);
    };

    const handleSetTestForMultipleTypes = (index, viewId, newValue) => {
        let data = _.cloneDeep(smartViewSet);
        let viewIndex = data.findIndex((v) => v.id === viewId);
        if (viewIndex === -1) return;
        if (Array.isArray(newValue)) {
            let mapValues = [];
            newValue.forEach((value) => {
                if (_.isObject(value)) {
                    mapValues.push(value.name)
                } else {
                    mapValues.push(value)
                }
            })
            handleSetTest(index, viewIndex, mapValues);
        } else {
            handleSetTest(index, viewIndex, newValue.name || newValue);
        }
    }

    const handleSetTestNew = (index, value) => {
        setNewSmartView((prev) => {
            prev.steps[index].test = value;

            if (index !== 0) {
                prev.steps[index].view = null;

                if (config.studiesType && config.studiesType[value])
                    prev.steps[index].view = config.studiesType[value]

                if (patientPreferencesObject.studiesTypeViews[value])
                    prev.steps[index].view = patientPreferencesObject.studiesTypeViews[value]
            }

            if (index === 0) prev.mainTest = value;
            return { ...prev };
        });
    };

    const handleSetNewTest = (index, newValue) => {
        if (Array.isArray(newValue)) {
            let mapValues = [];
            newValue.forEach((value) => {
                if (_.isObject(value)) {
                    mapValues.push(value.name)
                } else {
                    mapValues.push(value)
                }
            })
            handleSetTestNew(index, mapValues);
        } else {
            handleSetTestNew(index, newValue.name || newValue);
        }
    };

    const handleSetName = (viewId, value) => {
        let data = _.cloneDeep(smartViewSet);
        let viewIndex = data.findIndex((v) => v.id === viewId);
        if (viewIndex === -1) return;

        data[viewIndex].name = value;
        setSmartViewSet(data);
    };

    const handleSavePreferences = () => {
        let dataSmartViewSet = _.cloneDeep(smartViewSet);
        dataSmartViewSet = dataSmartViewSet.map((v) => {
            let newVersion = v.version || 1;
            const originalView = originalSmartViewSet.find(ov => ov.id === v.id);
            if (!_.isEqual(v, originalView)) {
                newVersion += 1;
            }
            return {
                ...v,
                version: newVersion
            };
        });

        setSmartViewSet(dataSmartViewSet);

        let data = _.cloneDeep(patientPreferencesObject);

        data.smartView = dataSmartViewSet;
        data.studiesTypeViews = displayImagePref;
        data.smartViewDefault = smartViewDefault;

        dispatch(savePref(data));
    }

    const handleSaveNewPreferences = () => {
        if (!newSmartView.name || newSmartView.name.trim() === "") {
            dispatch(setSnackbar({
                type: "error",
                message: t("Please enter a name for the Smart View")
            }));
            return;
        }

        let data = _.cloneDeep(patientPreferencesObject);
        let newSmartViewData = _.cloneDeep(newSmartView);

        let smartViewTmp = _.cloneDeep(smartViewSet);
        smartViewTmp.push(newSmartViewData);
        data.smartView = smartViewTmp;
        data.studiesTypeViews = displayImagePref;
        data.smartViewDefault = smartViewDefault;

        dispatch(savePref(data));
        closeModal();
    }

    const handleAddSmartItem = () => {
        setNewSmartView(smartViewItemInit);
        openModal();
    }

    const handleAddStep = (viewId) => {
        let data = _.cloneDeep(smartViewSet);
        let viewIndex = data.findIndex((v) => v.id === viewId);
        if (viewIndex === -1) return;

        let initValue = (data[viewIndex].steps.length === 0 ? [] : "");
        data[viewIndex].steps.push({
            test: initValue,
            type: "",
            view: null
        })
        setSmartViewSet(data);
    }

    const handleSetNewPreferences = (type, value) => {
        setDisplayImagePreferences((pref) => {
            pref[type.toString()] = value;
            return { ...pref }
        });
    };

    const filterDictType = (view) => {
        return docTypesDict.filter((option) => {
            let countItems = 0;
            view.steps.forEach((step) => {
                if (step.test === option.name) {
                    countItems++;
                }
            })
            return countItems < 2
        })
    }

    const handleSetNewDefaultSmartView = (option, id) => {
        let testName = option.name ? option.name : option;
        setSmartViewDefault((smartViewDef) => {
            smartViewDef[testName] = id;
            return { ...smartViewDef }
        });
    };

    const handleResetNewDefaultSmartView = (option, id) => {
        let testName = option.name ? option.name : option;
        setSmartViewDefault((smartViewDef) => {
            if (smartViewDef.hasOwnProperty(testName) && id === smartViewDef[testName])
                smartViewDef[testName] = null;
            return { ...smartViewDef }
        });
    };

    const handleResetToDefault = (view) => {
        let originalView = view.original_view;

        let data = _.cloneDeep(smartViewSet);
        data = data.map((v) => {
            if (v.id === view.id) {
                return _.cloneDeep({
                    ...view,
                    mainTest: _.cloneDeep(originalView.mainTest),
                    steps: _.cloneDeep(originalView.steps),
                    version: originalView.version,
                    name: originalView.name,
                });
            } else {
                return v;
            }
        });
        setSmartViewSet(data);

        let pref = _.cloneDeep(patientPreferencesObject);
        pref.smartView = data;
        pref.studiesTypeViews = displayImagePref;
        pref.smartViewDefault = smartViewDefault;
        dispatch(savePref(pref));
    }

    const isDefaultSmartView = (option, id) => {
        let testName = option.name ? option.name : option;
        return smartViewDefault.hasOwnProperty(testName) && smartViewDefault[testName] === id;
    }

    const handleExpanded = (id) => {
        setExpandedSet((prev) => {
            let newExpanded = { ...prev };
            if (newExpanded.hasOwnProperty(id)) {
                newExpanded[id] = !newExpanded[id];
            }
            else {
                newExpanded[id] = true;
            }
            return newExpanded;
        });
    }

    const handleShareSmartView = (users) => {
        if (!selectedSmartViewToShare) return;
        const view = {
            ...selectedSmartViewToShare,
            original_view: selectedSmartViewToShare,
            is_shared: true,
            shared_by_user: userObject.userName,
            shared_by_user_fullName: userObject.fullName,
            shared_date: new Date().toISOString(),
            id: uuidv4() + '',
            shared_list: users.map((user) => user.userName)
        }

        users.map((user) => {
            API.updateUserPreferenceShareSmartView(user.id, view).then(() => {
                dispatch(setSnackbar({
                    type: "success",
                    message: t("Smart View shared successfully with") + " " + user.fullName
                }));
            }).catch((error) => {
                dispatch(setSnackbar({
                    type: "error",
                    message: t("Failed to share Smart View with") + " " + user.fullName
                }));
            });
        });
    }

    const handleCopyFirstStep = () => {
        if (newSmartView.steps.length === 0) return;
        let firstStepTest = newSmartView.steps[0].test;
        let data = _.cloneDeep(newSmartView);

        firstStepTest.forEach((test) => {
            if(!data.steps.find((step) => step.test === test)){
                data.steps.push({
                    test: test,
                    type: "",
                    view: null
                })
            }
        });

        setNewSmartView(data);
    }

    return (
        <div className={classes.modalBody}>
            <CreateSmartViewDialog
                open={isModalOpen}
                onClose={closeModal}
                view={newSmartView}
                onUpdateName={(val) => setNewSmartView(prev => ({ ...prev, name: val }))}
                onSetTest={handleSetNewTest}
                onSetHistory={handleSetHistoryNew}
                onSetView={(test, val) => handleSetNewPreferences(test, val)}
                onDeleteStep={handleDeleteNewStep}
                onAddStep={() => {
                    let data = _.cloneDeep(newSmartView);
                    let initValue = (newSmartView.steps.length === 0 ? [] : "");
                    data.steps.push({
                        test: initValue,
                        type: "",
                        view: null
                    })
                    setNewSmartView(data);
                }}
                copyFirstStepTest={handleCopyFirstStep}
                onSave={handleSaveNewPreferences}
                filterDictType={filterDictType}
                displayImagePref={displayImagePref}
                viewDisplay={viewDisplay}
                onSetDefault={handleSetNewDefaultSmartView}
                onResetDefault={handleResetNewDefaultSmartView}
                isDefault={isDefaultSmartView}
                onOpenDisplayDialog={()=>{
                    openDisplayDialog();
                }}
            />

            <ShareSmartViewDialog
                open={isModalShareOpen}
                onClose={closeShareModal}
                viewToShare={selectedSmartViewToShare}
                onShare={handleShareSmartView}
            />

            <div className={classes.title}>
                Smart View
            </div>
            <div className={classes.footer}>
                <div style={{ display: "inline-flex", alignItems: "center" }}>
                    <AgyleButton
                        size="small"
                        onClick={() => setViewFilter('all')}
                        style={{ borderRadius: 20, marginRight: 10 }}
                        variant={viewFilter === 'all' ? 'contained' : 'outlined'}
                    >
                        {t("All Sets ") + `${smartViewSet.length}`}
                    </AgyleButton>
                    <AgyleButton
                        size="small"
                        onClick={() => setViewFilter('shared')}
                        style={{ borderRadius: 20 }}
                        variant={viewFilter === 'shared' ? 'contained' : 'outlined'}
                    >
                        {t("Shared sets ") + `${smartViewSet.filter(view => view.is_shared).length}`}
                    </AgyleButton>
                </div>
                <AgyleButton
                    size="small"
                    onClick={handleAddSmartItem}
                    style={{ borderRadius: 20 }}
                >
                    Create new smart view
                </AgyleButton>
            </div>
            <div style={{ height: "calc(100vh - 150px)" }} className={"scroll-normal-white"}>
                {smartViewSet && smartViewSet.filter(view => {
                    if (viewFilter === 'shared') return view.is_shared;
                    return true;
                }).map((view, viewIndex) => {
                    const originalView = originalSmartViewSet.find(ov => ov.id === view.id);
                    const isChanged = !_.isEqual(view, originalView) || !_.isEqual(displayImagePref, patientPreferencesObject.studiesTypeViews) || (smartViewDefault && !_.isEqual(smartViewDefault, patientPreferencesObject.smartViewDefault));

                    return <SmartViewItem
                        key={view.id}
                        view={view}
                        viewIndex={viewIndex}
                        expanded={expandedSet[view.id] !== undefined ? expandedSet[view.id] : false}
                        onExpand={() => handleExpanded(view.id)}
                        onUpdateName={(val) => handleSetName(view.id, val)}
                        onDeleteView={handleDeleteSmartView}
                        onDuplicateView={(view) => {
                            handleCopySmartView(view);
                        }}
                        onShareView={(view) => {
                            setSelectedSmartViewToShare(view);
                            openShareModal();
                        }}
                        onResetView={(view) => {
                            handleResetToDefault(view);
                        }}
                        onAddStep={handleAddStep}
                        onSetTest={handleSetTestForMultipleTypes}
                        onSetHistory={handleSetHistory}
                        onSetView={handleSetNewPreferences}
                        onDeleteStep={handleDeleteStep}
                        onSetDefault={handleSetNewDefaultSmartView}
                        onResetDefault={handleResetNewDefaultSmartView}
                        isDefault={isDefaultSmartView}
                        onOpenDisplayDialog={(id) => {
                            let index = smartViewSet.findIndex((sv) => sv.id === id);
                            setSelectedStepToManage(index);
                            openDisplayDialog();
                        }}
                        filterDictType={filterDictType}
                        classesBodyStyle={classes.bodyStyle}
                        onSave={handleSavePreferences}
                        displayImagePref={displayImagePref}
                        viewDisplay={viewDisplay}
                        isChanged={isChanged}
                    />
                })}
            </div>

            <ImageDisplayDialog
                open={isDisplayDialogOpen}
                onClose={()=> {
                    setSelectedStepToManage(null);
                    closeDisplayDialog();
                }}
                tests={selectedStepToManage != null && smartViewSet[selectedStepToManage] ?
                            smartViewSet[selectedStepToManage].steps[0].test :
                    newSmartView != null && newSmartView.steps[0] ?
                        newSmartView.steps[0].test : []
                }
                displayImagePref={displayImagePref}
                viewDisplay={viewDisplay}
                onSetNewPreferences={handleSetNewPreferences}
                classesDialogContentStyle={classes.dialogContentStyle}
            />

            <MessageBox
                title={t("Confirmation")}
                haveAccept={{yes: t("Delete"), no: t("Close")}}
                onAccept={doDeleteSmartViewCallback}
                onClose={closeDeleteSmartViewModal}
                message={deleteSmartViewMessage}
                open={isDeleteSmartViewModalOpen}
                disableOnAccept
                warning
            />
        </div>
    );
};

export default SmartView;